// ── 정산서 페이지 공통 상태·이펙트·핸들러 훅 ──────────────────────────────────
// PINE, DOT 모두 이 훅 하나로 처리.
// companyCode에 따라 API 경로만 달라지고, 나머지 로직은 완전히 동일.
// 새 회사 추가 시 SETTLEMENT_CONFIG에 항목만 추가하면 됨.

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';

// ── 회사별 설정 ─────────────────────────────────────────────────────────────
// baseApi:          정산서 기본 API 경로 (unmatched, records 경로도 이 경로에 이어 붙임)
// revenueApi:       결제 연결/해제 API 경로
// heroLabel:        모바일 히어로 헤더에 표시할 라벨
// unmatchedCardAmt: 미연결 카드승인 금액 필드 추출 함수 (테이블마다 컬럼명 다름)
const SETTLEMENT_CONFIG = {
  PINE: {
    baseApi:          '/api/my/settlement',
    revenueApi:       '/api/my/revenue',
    heroLabel:        '정산받을돈',
    unmatchedCardAmt: (i) => i.transaction_amount, // revenue_card_approvals
  },
  DOT: {
    baseApi:          '/api/my/settlement/dot',
    revenueApi:       '/api/my/revenue/dot',
    heroLabel:        '정산받을돈 (DOT)',
    unmatchedCardAmt: (i) => i.transaction_amount, // revenue_dot_card_approvals (수기 dot_revenue_cards 통일)
  },
  LNJ: {
    baseApi:          '/api/my/settlement/lnj',
    revenueApi:       '/api/my/revenue',           // PINE 공용 링크 라우트 (card_upload 지원)
    heroLabel:        '정산받을돈 (LNJ)',
    unmatchedCardAmt: (i) => i.transaction_amount, // ext_revenue_card_approvals
  },
  // 새 회사 추가 시 여기에 항목만 추가
};

export function useSettlement(companyCode = 'PINE') {
  // companyCode에 맞는 설정, 없으면 PINE 폴백
  const cfg = SETTLEMENT_CONFIG[companyCode] || SETTLEMENT_CONFIG.PINE;
  const { showToast } = useToast();

  // ── 반응형: 768px 이하 → 모바일, 초과 → PC ──────────────────────────────────
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // PC 전용 서브탭 ('stores' | 'timeline')
  const [pcSubTab, setPcSubTab] = useState('stores');

  // ── 정산서 데이터 ────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState({ totalMinus: 0, totalPlus: 0, netTotal: 0 });

  // 미연결 결제 (드래그 연결 UI용 — snake_case 원본)
  const [umLoading, setUmLoading] = useState(false);
  const [unmatched, setUnmatched] = useState({ payments: [], phone: [], card: [], card_upload: [] });
  // 날짜순 뷰용 미연결 결제 (camelCase + commission)
  const [unmatchedRev, setUnmatchedRev] = useState({ cards: [], phones: [], deposits: [] });

  // 정산지급 기록
  const [records, setRecords] = useState([]);

  // 계약 취소/복원 이벤트 (날짜순 타임라인용)
  const [cancelEvents, setCancelEvents] = useState([]);

  // 상담자 배분 수입 (내 상담자를 쓴 타 마케터 매장으로부터의 수입)
  const [shareIncome, setShareIncome] = useState({ items: [], total: 0 });

  // ── UI 상태 ──────────────────────────────────────────────────────────────────
  const [subTab,      setSubTab]      = useState('stores');  // 매장카드 | 날짜순
  const [activeTab,   setActiveTab]   = useState('stores');  // 담당매장 | 미연결결제
  const [expandedId,  setExpandedId]  = useState(null);      // 펼쳐진 매장 카드 ID
  const [selectedPay, setSelectedPay] = useState(null);      // 선택된 결제 { type, id }
  const [dragItem,    setDragItem]    = useState(null);       // 드래그 중 결제
  const [dragOverId,  setDragOverId]  = useState(null);       // 드래그 오버 매장 ID
  const [linking,     setLinking]     = useState(false);      // 연결 처리 중

  // ── 데이터 로드 ──────────────────────────────────────────────────────────────
  const fetchSettlement = useCallback(async () => {
    setLoading(true);
    const r = await apiClient.get(cfg.baseApi);
    setLoading(false);
    if (!r.ok) { showToast(r.data?.message || '정산서 조회 실패', 'error'); return; }
    setClients(Array.isArray(r.data?.clients) ? r.data.clients : []);
    if (r.data?.summary) setSummary(r.data.summary);
    // 날짜순 뷰용 unmatchedRevenue(camelCase + commission) 저장
    if (r.data?.unmatchedRevenue) {
      console.log('[useSettlement] unmatchedRevenue.others:', r.data.unmatchedRevenue.others);
      setUnmatchedRev(r.data.unmatchedRevenue);
    }
    // 계약 취소/복원 이벤트 저장 (날짜순 타임라인용)
    if (Array.isArray(r.data?.cancelEvents)) setCancelEvents(r.data.cancelEvents);
    // 상담자 배분 수입 저장
    if (r.data?.consultantShareIncome) setShareIncome(r.data.consultantShareIncome);
  }, [cfg.baseApi, showToast]);

  const fetchUnmatched = useCallback(async () => {
    setUmLoading(true);
    const r = await apiClient.get(`${cfg.baseApi}/unmatched`);
    setUmLoading(false);
    if (!r.ok) return;
    setUnmatched(r.data?.unmatched || { payments: [], phone: [], card: [], card_upload: [] });
  }, [cfg.baseApi]);

  const fetchRecords = useCallback(async () => {
    const r = await apiClient.get(`${cfg.baseApi}/records`);
    if (r.ok) setRecords(r.data?.records || []);
  }, [cfg.baseApi]);

  useEffect(() => {
    fetchSettlement();
    fetchUnmatched();
    fetchRecords();
  }, [fetchSettlement, fetchUnmatched, fetchRecords]);

  // ── 연결 / 해제 ──────────────────────────────────────────────────────────────
  async function handleLink(type, id, clientId) {
    if (linking) return;
    setLinking(true);
    const r = await apiClient.patch(`${cfg.revenueApi}/${type}/${id}/link`, { clientId });
    setLinking(false);
    if (!r.ok) { showToast(r.data?.message || '연결 실패', 'error'); return; }
    setSelectedPay(null);
    setDragItem(null);
    await Promise.all([fetchSettlement(), fetchUnmatched()]);
  }

  async function handleUnlink(type, id) {
    const r = await apiClient.patch(`${cfg.revenueApi}/${type}/${id}/unlink`);
    if (!r.ok) { showToast(r.data?.message || '연결 해제 실패', 'error'); return; }
    await Promise.all([fetchSettlement(), fetchUnmatched()]);
  }

  // ── 드래그 앤 드롭 핸들러 (데스크탑) ─────────────────────────────────────────
  function handleDragStart(e, payment) {
    setDragItem(payment);
    e.dataTransfer.effectAllowed = 'move';
  }
  function handleDragOver(e, clientId) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverId !== clientId) setDragOverId(clientId);
  }
  function handleDragLeave(e) {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOverId(null);
  }
  function handleDrop(e, clientId) {
    e.preventDefault();
    setDragOverId(null);
    if (dragItem) handleLink(dragItem.type, dragItem.id, clientId);
  }

  // ── 탭 선택 연결 (모바일) ─────────────────────────────────────────────────────
  function handlePaymentClick(payment) {
    const isSame = selectedPay?.type === payment.type && selectedPay?.id === payment.id;
    setSelectedPay(isSame ? null : payment);
    // 결제 선택 후 담당매장 탭으로 자동 전환 (연결 유도)
    if (!isSame) setActiveTab('stores');
  }

  function handleStoreCardClick(clientId) {
    if (!selectedPay) return;
    handleLink(selectedPay.type, selectedPay.id, clientId);
  }

  return {
    isMobile,
    pcSubTab, setPcSubTab,
    loading, clients, summary,
    umLoading, unmatched, unmatchedRev,
    records, cancelEvents, shareIncome,
    subTab, setSubTab,
    activeTab, setActiveTab,
    expandedId, setExpandedId,
    selectedPay, setSelectedPay,
    dragItem, dragOverId,
    fetchSettlement, fetchUnmatched,
    handleLink, handleUnlink,
    handleDragStart, handleDragOver, handleDragLeave, handleDrop,
    handlePaymentClick, handleStoreCardClick,
    cfg,  // PcView / MobileView에서 heroLabel, unmatchedCardAmt 등 참조용
  };
}
